import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDownload, faFileContract, faShieldHalved } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { EContractProfile } from "../../Enum/EContractProfile"
import { useDownloadContract } from "../../hooks/Contract/useDownloadContract"
import Animate, { AnimationTypes } from "../Animate/Animate"

interface ContractDownloadsCardProps {
  showAutomaticAcceptance?: boolean
}

const contracts = [
  {
    profile: EContractProfile.RestaurantOwner,
    translationKey: "restaurant_owner",
  },
  {
    profile: EContractProfile.Courier,
    translationKey: "courier",
  },
  {
    profile: EContractProfile.Customer,
    translationKey: "customer",
  },
] as const

export const ContractDownloadsCard = ({ showAutomaticAcceptance = false }: ContractDownloadsCardProps) => {
  const { t } = useTranslation("contracts")

  const { handleDownloadContractAsync } = useDownloadContract()

  return (
    <Animate duration="1.5s" type={AnimationTypes.fadeInTraslate}>
      <section className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 via-white to-red-50 p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-red-600 to-orange-500 text-white shadow-sm">
            <FontAwesomeIcon icon={faFileContract} />
          </span>

          <div className="flex-1">
            <h2 className="text-lg font-extrabold text-slate-900 sm:text-xl">{t("title")}</h2>

            <p className="mt-1 text-sm leading-relaxed text-slate-600">{t("description")}</p>

            {showAutomaticAcceptance && (
              <div className="mt-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50/80 p-4 text-sm leading-relaxed text-red-900">
                <FontAwesomeIcon className="mt-0.5 shrink-0 text-red-600" icon={faShieldHalved} />
                <p className="font-semibold">{t("automatic_acceptance")}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {contracts.map((contract) => (
            <button
              key={contract.profile}
              type="button"
              onClick={() => handleDownloadContractAsync(contract.profile)}
              className="flex min-h-20 items-center justify-between gap-3 rounded-xl border border-orange-200 bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-red-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              data-interaction-description-key="interactionDescriptions:components.contractDownloads.contractDownloadsCard.button.handleDownloadContractAsyncContractProfile"
            >
              <span>
                <span className="block text-sm font-extrabold text-slate-900">
                  {t(`items.${contract.translationKey}.title`)}
                </span>

                <span className="mt-1 block text-xs text-slate-600">
                  {t(`items.${contract.translationKey}.description`)}
                </span>
              </span>

              <FontAwesomeIcon className="shrink-0 text-red-600" icon={faDownload} />
            </button>
          ))}
        </div>
      </section>
    </Animate>
  )
}
